import { combineActions, handleActions } from 'redux-actions';
import { createSelector, Reducer } from '@reduxjs/toolkit';
import { getMobileBrowserOS, getUiBrowser, isUiTablet } from './browser';
import {
    LIVE_ASK_CHANGED,
    LIVE_ASK_CHANGED_ACTION,
    LIVE_AUCTION_ENDED,
    LIVE_AUCTION_ENDED_ACTION,
    LIVE_AUCTION_MOVED,
    LIVE_AUCTION_MOVED_ACTION,
    LIVE_AUCTION_OPENED,
    LIVE_AUCTION_OPENED_ACTION,
    LIVE_AUCTION_PAUSED,
    LIVE_AUCTION_PAUSED_ACTION,
    LIVE_AUCTION_RESUMED,
    LIVE_AUCTION_RESUMED_ACTION,
    LIVE_AUCTION_STARTED,
    LIVE_AUCTION_STARTED_ACTION,
    LIVE_BID_ACCEPTED,
    LIVE_BID_ACCEPTED_ACTION,
    LIVE_BID_RETRACTED,
    LIVE_BID_RETRACTED_ACTION,
    LIVE_LOT_CLOSED,
    LIVE_LOT_CLOSED_ACTION,
    LIVE_LOT_PASSED,
    LIVE_LOT_PASSED_ACTION,
    LIVE_LOT_REOPENED,
    LIVE_LOT_REOPENED_ACTION,
    LIVE_LOT_SOLD,
    LIVE_LOT_SOLD_ACTION,
    LIVE_LOT_UNSOLD,
    LIVE_LOT_UNSOLD_ACTION,
    LIVE_MISSIVE,
    LIVE_MISSIVE_ACTION,
    LIVE_NETWORK_DISCONNECTED,
    LIVE_NETWORK_DISCONNECTED_ACTION,
    LIVE_NETWORK_RECONNECTED,
    LIVE_NETWORK_RECONNECTED_ACTION,
    LIVE_NEXT_LOT_LOADED,
    LIVE_NEXT_LOT_LOADED_ACTION,
    LIVE_UPDATE_ACTIVE_BIDDERS_PUBNUB,
    LIVE_UPDATE_ACTIVE_BIDDERS_PUBNUB_ACTION,
    LIVE_UPDATE_TIMESTAMP,
    LIVE_UPDATE_TIMESTAMP_ACTION,
    LOAD_CATALOGS_SUCCESS,
    LOAD_CATALOGS_SUCCESS_ACTION,
    LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS,
    LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS_ACTION,
    LOAD_LIVE_CATALOG_STATUS_SUCCESS,
    LOAD_LIVE_CATALOG_STATUS_SUCCESS_ACTION,
} from './actions';
import cloneDeep from 'lodash/cloneDeep';
import { GlobalState } from '../rootReducer';
import { LiveCatalogData } from '@/types/LiveCatalogData';
import { LiveItemData } from '@/types/LiveItemData';

/* helpers */
const getCurrentTimestamp = () => new Date().getTime();

export interface State {
    readonly catalogs: {
        [catalogId: number]: LiveCatalogData;
    };
    readonly items: {
        [itemId: number]: LiveItemData;
    };
    readonly timestamps: {
        [catalogId: number]: number;
    };
}

export const DEFAULT_STATE: State = {
    catalogs: {},
    items: {},
    timestamps: {},
};

export const DEFAULT_CATALOG: LiveCatalogData = {
    activeBidder: false,
    approved: false,
    bidderOutbid: false,
    bidders: 0, // this is computed in the selector
    closed: true,
    currentItem: 0,
    currentItemIndex: 0,
    networkStatus: 'connected',
    pubnubBidderCount: 0,
    pubNubPublishKey: '',
    sfsBidderCount: 0,
    status: 'notLoaded',
    won: false,
};

export const DEFAULT_ITEM: LiveItemData = {
    bidCount: 0,
    created: getCurrentTimestamp(),
    currentAskPrice: 0,
    leadingBid: 0,
    leadingBidder: 0,
};

const setCatalogLive = (state: State, catalogId: number): State['catalogs'] => {
    const catalogs = cloneDeep(state.catalogs);
    catalogs[catalogId] = cloneDeep(catalogs[catalogId] || DEFAULT_CATALOG);
    catalogs[catalogId].status = 'live';
    return catalogs;
};

const console: Reducer<State> = handleActions(
    {
        [LIVE_ASK_CHANGED]: (state: State, action: LIVE_ASK_CHANGED_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
            catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;

            const items = cloneDeep(state.items);
            items[action.payload.itemId] = items[action.payload.itemId] || DEFAULT_ITEM;
            items[action.payload.itemId].currentAskPrice = action.payload.ask;

            return {
                ...state,
                catalogs,
                items,
            };
        },
        [LIVE_AUCTION_ENDED]: (state: State, action: LIVE_AUCTION_ENDED_ACTION): State => {
            const catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = cloneDeep(catalogs[action.payload.catalogId] || DEFAULT_CATALOG);
            catalogs[action.payload.catalogId].status = 'done';

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_AUCTION_MOVED]: (state: State, action: LIVE_AUCTION_MOVED_ACTION): State => {
            const catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = cloneDeep(catalogs[action.payload.catalogId] || DEFAULT_CATALOG);
            catalogs[action.payload.catalogId].status = 'notLoaded';

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_AUCTION_OPENED]: (state: State, action: LIVE_AUCTION_OPENED_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_AUCTION_PAUSED]: (state: State, action: LIVE_AUCTION_PAUSED_ACTION): State => {
            const catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = cloneDeep(catalogs[action.payload.catalogId] || DEFAULT_CATALOG);
            catalogs[action.payload.catalogId].status = 'paused';

            return {
                ...state,
                catalogs,
            };
        },
        [combineActions(LIVE_AUCTION_RESUMED, LIVE_AUCTION_STARTED)]: (
            state: State,
            action: LIVE_AUCTION_RESUMED_ACTION | LIVE_AUCTION_STARTED_ACTION
        ): State => {
            const catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = cloneDeep(catalogs[action.payload.catalogId] || DEFAULT_CATALOG);
            catalogs[action.payload.catalogId].status = 'paused';

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_BID_ACCEPTED]: (state: State, action: LIVE_BID_ACCEPTED_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].activeBidder =
                Boolean(catalogs[action.payload.catalogId].activeBidder) || Boolean(action.payload.myBid);
            catalogs[action.payload.catalogId].bidderOutbid = Boolean(action.payload.bidderOutbid);
            catalogs[action.payload.catalogId].closed = false;
            catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
            catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;

            const items = cloneDeep(state.items);
            items[action.payload.itemId] = cloneDeep(
                items[action.payload.itemId] || {
                    ...DEFAULT_ITEM,
                    created: getCurrentTimestamp(),
                }
            );

            if (items[action.payload.itemId].created < action.payload.created) {
                items[action.payload.itemId].created = action.payload.created;
                items[action.payload.itemId].currentAskPrice = action.payload.ask;
                items[action.payload.itemId].leadingBid = action.payload.amount;
                items[action.payload.itemId].leadingBidder = action.payload.bidderId;
                items[action.payload.itemId].bidCount = items[action.payload.itemId].bidCount
                    ? items[action.payload.itemId].bidCount + 1
                    : 1;
            }

            return {
                ...state,
                catalogs,
                items,
            };
        },
        [LIVE_BID_RETRACTED]: (state: State, action: LIVE_BID_RETRACTED_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].bidderOutbid = false;
            catalogs[action.payload.catalogId].closed = false;
            catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
            catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;

            const items = cloneDeep(state.items);
            items[action.payload.itemId] = cloneDeep(items[action.payload.itemId] || DEFAULT_ITEM);
            items[action.payload.itemId].created = action.payload.created;
            items[action.payload.itemId].currentAskPrice = action.payload.ask;
            items[action.payload.itemId].leadingBid = action.payload.amount;
            items[action.payload.itemId].leadingBidder = action.payload.bidderId;
            items[action.payload.itemId].bidCount =
                items[action.payload.itemId].bidCount > 0 ? items[action.payload.itemId].bidCount - 1 : 0;

            return {
                ...state,
                catalogs,
                items,
            };
        },
        [LIVE_LOT_CLOSED]: (state: State, action: LIVE_LOT_CLOSED_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].bidderOutbid = false;

            if (catalogs[action.payload.catalogId].currentItem === action.payload.itemId) {
                catalogs[action.payload.catalogId].closed = true;
            }

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_LOT_PASSED]: (state: State, action: LIVE_LOT_PASSED_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].bidderOutbid = false;
            catalogs[action.payload.catalogId].closed = true;

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_LOT_REOPENED]: (state: State, action: LIVE_LOT_REOPENED_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].closed = false; // if from new system set variables, else don't -- old message does not have askprice

            if (
                action.payload.askPrice !== null &&
                action.payload.askPrice !== undefined &&
                action.payload.askPrice !== 0
            ) {
                catalogs[action.payload.catalogId].activeBidder =
                    Boolean(catalogs[action.payload.catalogId].activeBidder) || Boolean(action.payload.myBid);
                catalogs[action.payload.catalogId].bidderOutbid = false;
                catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
                catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;
                catalogs[action.payload.catalogId].won = false;

                const items = cloneDeep(state.items);

                items[action.payload.itemId] = cloneDeep(items[action.payload.itemId] || DEFAULT_ITEM);
                items[action.payload.itemId].created = action.payload.created;
                items[action.payload.itemId].currentAskPrice = action.payload.askPrice;
                items[action.payload.itemId].bidCount = 0;
                items[action.payload.itemId].leadingBid = action.payload.leadingBid;
                items[action.payload.itemId].leadingBidder = action.payload.leadingBidder;

                return {
                    ...state,
                    catalogs,
                    items,
                };
            } else {
                return {
                    ...state,
                    catalogs,
                };
            }
        },
        [LIVE_LOT_SOLD]: (state: State, action: LIVE_LOT_SOLD_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].bidderOutbid = false;
            catalogs[action.payload.catalogId].closed = true;
            catalogs[action.payload.catalogId].won = action.payload.myBid;

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_LOT_UNSOLD]: (state: State, action: LIVE_LOT_UNSOLD_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].bidderOutbid = false;
            catalogs[action.payload.catalogId].closed = true;

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_MISSIVE]: (state: State, action: LIVE_MISSIVE_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_NETWORK_DISCONNECTED]: (state: State, action: LIVE_NETWORK_DISCONNECTED_ACTION): State => {
            const catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = cloneDeep(catalogs[action.payload.catalogId] || DEFAULT_CATALOG);
            catalogs[action.payload.catalogId].networkStatus = 'disconnected';

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_NETWORK_RECONNECTED]: (state: State, action: LIVE_NETWORK_RECONNECTED_ACTION): State => {
            const catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = cloneDeep(catalogs[action.payload.catalogId] || DEFAULT_CATALOG);
            catalogs[action.payload.catalogId].networkStatus = 'connected';

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_NEXT_LOT_LOADED]: (state: State, action: LIVE_NEXT_LOT_LOADED_ACTION): State => {
            const catalogs = setCatalogLive(state, action.payload.catalogId);
            catalogs[action.payload.catalogId].activeBidder = false;
            catalogs[action.payload.catalogId].bidderOutbid = false;
            catalogs[action.payload.catalogId].closed = false;
            catalogs[action.payload.catalogId].currentItem = action.payload.itemId;
            catalogs[action.payload.catalogId].currentItemIndex = action.payload.itemIndex;
            catalogs[action.payload.catalogId].won = false;

            const items = cloneDeep(state.items);
            items[action.payload.itemId] = cloneDeep(items[action.payload.itemId] || DEFAULT_ITEM);
            items[action.payload.itemId].created = action.payload.created;
            items[action.payload.itemId].currentAskPrice = action.payload.askPrice;
            items[action.payload.itemId].bidCount = 0;
            items[action.payload.itemId].leadingBid = 0;
            items[action.payload.itemId].leadingBidder = 0;

            return {
                ...state,
                catalogs,
                items,
            };
        },
        [LIVE_UPDATE_ACTIVE_BIDDERS_PUBNUB]: (
            state: State,
            action: LIVE_UPDATE_ACTIVE_BIDDERS_PUBNUB_ACTION
        ): State => {
            const catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = cloneDeep(catalogs[action.payload.catalogId] || DEFAULT_CATALOG);
            catalogs[action.payload.catalogId].pubnubBidderCount = action.payload.bidderCount;

            return {
                ...state,
                catalogs,
            };
        },
        [LIVE_UPDATE_TIMESTAMP]: (state: State, action: LIVE_UPDATE_TIMESTAMP_ACTION): State => {
            return {
                ...state,
                timestamps: {
                    ...state.timestamps,
                    [action.payload.catalogId]: action.payload.timestamp,
                },
            };
        },
        [LOAD_CATALOGS_SUCCESS]: (state: State, action: LOAD_CATALOGS_SUCCESS_ACTION): State => {
            const catalogs = cloneDeep(state.catalogs);

            action.payload.catalogs.forEach((catalog) => {
                if (catalog.catalogStatus === 'done') {
                    // set the catalog to done if it is
                    catalogs[catalog.catalogId] = cloneDeep(catalogs[catalog.catalogId] || DEFAULT_CATALOG);
                    catalogs[catalog.catalogId].status = catalog.catalogStatus;
                }
            });

            return {
                ...state,
                catalogs,
            };
        },
        [LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS]: (
            state: State,
            action: LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS_ACTION
        ): State => {
            const catalogs = cloneDeep(state.catalogs);
            catalogs[action.payload.catalogId] = cloneDeep(catalogs[action.payload.catalogId] || DEFAULT_CATALOG);

            if (
                catalogs[action.payload.catalogId].pubNubPublishKey !== action.payload.publishKey ||
                catalogs[action.payload.catalogId].approved !== action.payload.approved
            ) {
                catalogs[action.meta.catalogId].approved = action.payload.approved;
                catalogs[action.meta.catalogId].pubNubPublishKey = action.payload.publishKey;
                return {
                    ...state,
                    catalogs,
                };
            }

            return state;
        },
        [LOAD_LIVE_CATALOG_STATUS_SUCCESS]: (state: State, action: LOAD_LIVE_CATALOG_STATUS_SUCCESS_ACTION): State => {
            if (action.payload.status !== 'notLoaded') {
                const catalogs = cloneDeep(state.catalogs);
                catalogs[action.payload.catalogId] = cloneDeep(catalogs[action.payload.catalogId] || DEFAULT_CATALOG);

                if (catalogs[action.payload.catalogId].status !== 'done') {
                    catalogs[action.payload.catalogId].status = action.payload.status;
                }

                const items = cloneDeep(state.items);

                if (action.payload.currentItem && action.payload.currentItem.itemId) {
                    const { askPrice, itemId, itemIndex, leadingBid, leadingBidder } = action.payload.currentItem;

                    catalogs[action.payload.catalogId].closed = false;
                    catalogs[action.payload.catalogId].currentItem = itemId;
                    catalogs[action.payload.catalogId].currentItemIndex = itemIndex || 0;
                    catalogs[action.payload.catalogId].won = false;

                    items[itemId] = items[itemId] || DEFAULT_ITEM;
                    items[itemId].created = getCurrentTimestamp();
                    items[itemId].currentAskPrice = askPrice;
                    items[itemId].leadingBid = leadingBid || 0;
                    items[itemId].leadingBidder = leadingBidder || 0;
                }

                return {
                    ...state,
                    catalogs,
                    items,
                };
            } else {
                return { ...state };
            }
        },
    },
    DEFAULT_STATE
);

export default console;

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.console;
const idSelector = (state: GlobalState, id: number) => id;

const catalogsSelector = createSelector(stateSelector, (state) => state.catalogs);
const itemsSelector = createSelector(stateSelector, (state) => state.items);
const timestampSelector = createSelector(stateSelector, (state) => state.timestamps);

export const getCatalogNetworkStatus = createSelector([catalogsSelector, idSelector], (catalogs, id) => {
    return catalogs[id]?.networkStatus || 'connected';
});

export const getAllLiveCatalogs = createSelector(stateSelector, (state) => state.catalogs);

export const getLatestMessageTimestamp = createSelector(
    [timestampSelector, idSelector],
    (timestamps, id) => timestamps[id] || 0
);

export const getPubnubPublishKey = createSelector([catalogsSelector, idSelector], (catalogs, id): string => {
    return catalogs[id]?.pubNubPublishKey || '';
});

export const getLiveCatalogData = createSelector([catalogsSelector, idSelector], (catalogs, id): LiveCatalogData => {
    const built = { ...DEFAULT_CATALOG, ...(catalogs[id] || {}) };
    built.bidders = built.sfsBidderCount + built.pubnubBidderCount || 1;
    return built;
});

export const getLiveItemData = createSelector(
    [itemsSelector, idSelector],
    (items, id): LiveItemData => items[id] || DEFAULT_ITEM
);

export const getBrowserSessionCookieSuffix = (state: GlobalState) => {
    const mobileBrowser = getMobileBrowserOS(state);
    const tablet = isUiTablet(state);
    const { majorVersion, name } = getUiBrowser(state);
    const browser = `${name}${majorVersion}`;

    const deviceType = !mobileBrowser ? 'computer' : tablet ? 'tablet' : 'phone';
    return `::web-${deviceType}-${browser}`;
};
